import React, { useEffect, useState } from 'react';
import { CircleMarker, Circle, Popup } from 'react-leaflet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMapMarkerAlt,
  faCircle,
  faExclamationTriangle,
} from '@fortawesome/pro-regular-svg-icons';
import { useGeolocation } from '../../../hooks/useGeolocation';
import {
  useAppDispatch,
  useAppSelector,
  setCurrentLocation,
  isTrackingLocationSelector,
  showCurrentLocationSelector,
  serializeGeolocationPosition,
} from '../../../store';
import './CurrentLocationMarker.scss';

export const CurrentLocationMarker: React.FC = () => {
  const dispatch = useAppDispatch();
  const showCurrentLocation = useAppSelector(showCurrentLocationSelector);
  const enableTracking = useAppSelector(isTrackingLocationSelector);

  const {
    position,
    error,
    isLoading,
    isWatching,
    accuracy,
    retry
  } = useGeolocation({
    watch: enableTracking,
    enableHighAccuracy: true,
    timeout: 10000,
    maximumAge: 60000
  });

  const [isPulsing, setIsPulsing] = useState(false);

  useEffect(() => {
    if (position) {
      setIsPulsing(true);
      const timer = setTimeout(() => setIsPulsing(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [position]);

  useEffect(() => {
    if (position) {
      dispatch(setCurrentLocation(serializeGeolocationPosition(position)));
    }
  }, [position, dispatch]);

  if (!showCurrentLocation || !position) {
    return null;
  }

  const { latitude, longitude } = position.coords;
  const accuracyRadius = accuracy || position.coords.accuracy;

  const formatAccuracy = (acc: number): string => {
    if (acc < 1000) {
      return `${Math.round(acc)}m`;
    }
    return `${(acc / 1000).toFixed(1)}km`;
  };

  const formatCoordinates = (lat: number, lng: number): string => {
    return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
  };

  const formatTimestamp = (timestamp: number): string => {
    return new Date(timestamp).toLocaleTimeString();
  };

  return (
    <>
      {/* Accuracy Circle */}
      {accuracyRadius && (
        <Circle
          center={[latitude, longitude]}
          radius={accuracyRadius}
          pathOptions={{
            color: '#3388ff',
            fillColor: '#3388ff',
            fillOpacity: 0.1,
            weight: 1,
            opacity: 0.3
          }}
        />
      )}

      {/* Current Location Marker */}
      <CircleMarker
        center={[latitude, longitude]}
        radius={8}
        pathOptions={{
          color: '#ffffff',
          fillColor: '#3388ff',
          fillOpacity: 1,
          weight: 2,
          className: isPulsing ? 'current-location-marker pulsing' : 'current-location-marker'
        }}
      >
        <Popup>
          <article className="current-location-popup">
            <header className="popup-header">
              <FontAwesomeIcon icon={faMapMarkerAlt} className="location-icon" aria-hidden="true" />
              <h2>Current Location</h2>
              {isWatching && (
                <span className="tracking-indicator" title="Live tracking enabled">
                  <FontAwesomeIcon icon={faCircle} className="live-indicator" aria-hidden="true" />
                </span>
              )}
            </header>

            <dl className="location-details">
              <div className="coordinate-info">
                <dt>Coordinates:</dt>
                <dd>{formatCoordinates(latitude, longitude)}</dd>
              </div>

              {accuracyRadius && (
                <div className="accuracy-info">
                  <dt>Accuracy:</dt>
                  <dd>{formatAccuracy(accuracyRadius)}</dd>
                </div>
              )}

              <div className="timestamp-info">
                <dt>Updated:</dt>
                <dd>
                  <time dateTime={new Date(position.timestamp).toISOString()}>
                    {formatTimestamp(position.timestamp)}
                  </time>
                </dd>
              </div>

              {position.coords.speed !== null && position.coords.speed > 0 && (
                <div className="speed-info">
                  <dt>Speed:</dt>
                  <dd>{(position.coords.speed * 3.6).toFixed(1)} km/h</dd>
                </div>
              )}
            </dl>

            {error && (
              <div className="error-info" role="alert">
                <span>
                  <FontAwesomeIcon icon={faExclamationTriangle} className="error-icon" aria-hidden="true" />
                  <span>{error}</span>
                </span>
                <button
                  className="retry-button"
                  onClick={retry}
                  disabled={isLoading}
                  aria-label={isLoading ? 'Retrying location request' : 'Retry location request'}
                >
                  {isLoading ? 'Retrying...' : 'Retry'}
                </button>
              </div>
            )}
          </article>
        </Popup>
      </CircleMarker>
    </>
  );
};
